import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  CalendarToday as ReportsIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Schedule as ScheduleIcon,
  Description as ReportIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const PeriodicReports = () => {
  const [filters, setFilters] = useState({
    period_type: 'monthly',
    period_value: new Date().toISOString().slice(0, 7),
    report_category: 'all'
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: reportsData, isLoading, error, refetch } = useQuery(
    ['periodicReports', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/reports/periodic-reports?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching periodic reports: ${error.message}`, { variant: 'error' });
      },
      enabled: false
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleGenerateReports = () => {
    refetch();
  };

  const handleViewReport = (reportType) => {
    enqueueSnackbar(`Opening ${reportType} report...`, { variant: 'info' });
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading periodic reports: {error.message}
        </Alert>
      </Box>
    );
  }

  const mockReportsData = [
    {
      category: 'Production',
      reports: [
        {
          name: 'Monthly Production Summary',
          description: 'Comprehensive production analysis including volume, efficiency, and capacity utilization',
          last_generated: '2024-11-30',
          status: 'ready',
          size: '2.4 MB',
          pages: 15,
          icon: <BarChartIcon />,
          color: 'primary'
        },
        {
          name: 'Heat Treatment Performance',
          description: 'HT process efficiency, furnace utilization, and quality metrics',
          last_generated: '2024-11-30',
          status: 'ready',
          size: '1.8 MB',
          pages: 12,
          icon: <TrendingUpIcon />,
          color: 'secondary'
        },
        {
          name: 'Die-wise Production Analysis',
          description: 'Production performance analysis by die number with trend analysis',
          last_generated: '2024-11-29',
          status: 'ready',
          size: '3.1 MB',
          pages: 22,
          icon: <AssessmentIcon />,
          color: 'info'
        }
      ]
    },
    {
      category: 'Quality',
      reports: [
        {
          name: 'Quality Metrics Dashboard',
          description: 'Defect rates, QFBR analysis, customer complaints, and corrective actions',
          last_generated: '2024-11-30',
          status: 'ready',
          size: '1.9 MB',
          pages: 18,
          icon: <PieChartIcon />,
          color: 'success'
        },
        {
          name: 'Material Test Certificate Summary',
          description: 'MTC compliance analysis, chemical composition trends, and supplier performance',
          last_generated: '2024-11-30',
          status: 'processing',
          size: '2.7 MB',
          pages: 25,
          icon: <AssessmentIcon />,
          color: 'warning'
        },
        {
          name: 'Microstructure Analysis Report',
          description: 'Metallurgical test results, grain size analysis, and microstructure compliance',
          last_generated: '2024-11-28',
          status: 'ready',
          size: '4.2 MB',
          pages: 35,
          icon: <ReportIcon />,
          color: 'error'
        }
      ]
    },
    {
      category: 'Raw Material',
      reports: [
        {
          name: 'Material Receipt Analysis',
          description: 'SRR processing, acceptance rates, supplier performance, and inventory status',
          last_generated: '2024-11-30',
          status: 'ready',
          size: '1.6 MB',
          pages: 14,
          icon: <BarChartIcon />,
          color: 'primary'
        },
        {
          name: 'Supplier Performance Report',
          description: 'Supplier ratings, delivery performance, quality metrics, and cost analysis',
          last_generated: '2024-11-29',
          status: 'ready',
          size: '2.1 MB',
          pages: 16,
          icon: <TrendingUpIcon />,
          color: 'secondary'
        }
      ]
    },
    {
      category: 'Management',
      reports: [
        {
          name: 'Executive Dashboard',
          description: 'High-level KPIs, trends, achievements, and strategic recommendations',
          last_generated: '2024-11-30',
          status: 'ready',
          size: '1.2 MB',
          pages: 8,
          icon: <AssessmentIcon />,
          color: 'info'
        },
        {
          name: 'Cost Analysis Report',
          description: 'Production costs, material costs, efficiency metrics, and cost optimization opportunities',
          last_generated: '2024-11-30',
          status: 'scheduled',
          size: '2.8 MB',
          pages: 20,
          icon: <PieChartIcon />,
          color: 'success'
        }
      ]
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'success';
      case 'processing': return 'warning';
      case 'scheduled': return 'info';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ready': return 'Ready to View';
      case 'processing': return 'Processing...';
      case 'scheduled': return 'Scheduled';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };

  const totalReports = mockReportsData.reduce((sum, category) => sum + category.reports.length, 0);
  const readyReports = mockReportsData.reduce((sum, category) => 
    sum + category.reports.filter(r => r.status === 'ready').length, 0);
  const totalPages = mockReportsData.reduce((sum, category) => 
    sum + category.reports.reduce((pageSum, report) => pageSum + report.pages, 0), 0);

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ReportsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          Periodic Reports Center
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Report Generation Settings</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Period Type</InputLabel>
              <Select
                value={filters.period_type}
                label="Period Type"
                onChange={(e) => {
                  const newType = e.target.value;
                  let newValue = '';
                  if (newType === 'monthly') {
                    newValue = new Date().toISOString().slice(0, 7);
                  } else if (newType === 'quarterly') {
                    const quarter = Math.ceil((new Date().getMonth() + 1) / 3);
                    newValue = `${new Date().getFullYear()}-Q${quarter}`;
                  } else if (newType === 'yearly') {
                    newValue = new Date().getFullYear().toString();
                  }
                  setFilters({ ...filters, period_type: newType, period_value: newValue });
                }}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {filters.period_type === 'monthly' && (
              <TextField
                fullWidth
                size="small"
                type="month"
                label="Month"
                InputLabelProps={{ shrink: true }}
                value={filters.period_value}
                onChange={(e) => setFilters({ ...filters, period_value: e.target.value })}
              />
            )}
            {filters.period_type === 'quarterly' && (
              <TextField
                fullWidth
                size="small"
                label="Quarter (YYYY-QN)"
                placeholder="2024-Q1"
                value={filters.period_value}
                onChange={(e) => setFilters({ ...filters, period_value: e.target.value })}
              />
            )}
            {filters.period_type === 'yearly' && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Year"
                value={filters.period_value}
                onChange={(e) => setFilters({ ...filters, period_value: e.target.value })}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.report_category}
                label="Category"
                onChange={(e) => setFilters({ ...filters, report_category: e.target.value })}
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="production">Production</MenuItem>
                <MenuItem value="quality">Quality</MenuItem>
                <MenuItem value="raw_material">Raw Material</MenuItem>
                <MenuItem value="management">Management</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReports}
              startIcon={<SearchIcon />}
            >
              Refresh Reports
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="primary">
                {totalReports}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Reports
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="success.main">
                {readyReports}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Ready to View
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="info.main">
                {totalPages}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Pages
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="secondary.main">
                {mockReportsData.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Reports by Category */}
          {mockReportsData.map((category, categoryIndex) => (
            <Paper key={categoryIndex} sx={{ mb: 3 }}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" color="primary">
                  {category.category} Reports
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {category.reports.length} report(s) available
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {category.reports.map((report, reportIndex) => (
                    <Grid item xs={12} md={6} lg={4} key={reportIndex}>
                      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: `${report.color}.main`, mr: 2 }}>
                              {report.icon}
                            </Avatar>
                            <Box>
                              <Typography variant="h6" component="div">
                                {report.name}
                              </Typography>
                              <Chip 
                                label={getStatusText(report.status)}
                                color={getStatusColor(report.status)}
                                size="small"
                              />
                            </Box>
                          </Box>
                          <Typography variant="body2" color="textSecondary" paragraph>
                            {report.description}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary">
                                Last Generated:
                              </Typography>
                              <Typography variant="body2">
                                {new Date(report.last_generated).toLocaleDateString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary">
                                Size:
                              </Typography>
                              <Typography variant="body2">
                                {report.size} ({report.pages} pages)
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                        <CardActions>
                          <Button 
                            size="small" 
                            color="primary"
                            disabled={report.status !== 'ready'}
                            onClick={() => handleViewReport(report.name)}
                          >
                            View Report
                          </Button>
                          <Button 
                            size="small" 
                            color="secondary"
                            disabled={report.status !== 'ready'}
                          >
                            Download
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          ))}

          {/* Report Generation Schedule */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Report Generation Schedule
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="primary">Daily Reports:</Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText 
                      primary="Production Summary" 
                      secondary="6:00 AM daily"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText 
                      primary="Quality Alerts" 
                      secondary="8:00 AM daily"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="secondary.main">Weekly Reports:</Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText 
                      primary="Supplier Performance" 
                      secondary="Monday 7:00 AM"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText 
                      primary="HT Analysis" 
                      secondary="Friday 6:00 PM"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="success.main">Monthly Reports:</Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText 
                      primary="Executive Summary" 
                      secondary="1st of each month"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText 
                      primary="Cost Analysis" 
                      secondary="5th of each month"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="warning.main">Quarterly Reports:</Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText 
                      primary="Strategic Review" 
                      secondary="End of quarter"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText 
                      primary="Audit Report" 
                      secondary="Mid quarter"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default PeriodicReports;